/**
 * Home visit patient list API service (訪問予定表 患者毎).
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type {
  HomeVisitPatientSearchParams,
  HomeVisitPatientSearchResponse,
  HomeVisitPatientMassagersResponse,
  HomeVisitPatientMassagerFilterParams,
  PrintHomeVisitPatientPayload,
  ExportHomeVisitPatientCsvPayload,
} from '@/pages/main/homeVisitPatient/types';

function toParams(
  filters: HomeVisitPatientSearchParams
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value as string | number;
    }
  });
  return params;
}

/**
 * Search patients grouped by patient with visits in date range.
 */
export async function searchHomeVisitPatients(
  siteType: string,
  clinicNumber: string,
  filters: HomeVisitPatientSearchParams,
  serviceType: string = SERVICE_TYPE_A2
): Promise<HomeVisitPatientSearchResponse> {
  const response = await apiClient.get<HomeVisitPatientSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/home-visit-patients/search'),
    {
      params: toParams(filters),
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Get massagers for dropdown filtered by selected visit datetime range.
 */
export async function getHomeVisitPatientMassagers(
  siteType: string,
  clinicNumber: string,
  serviceType: string = SERVICE_TYPE_A2,
  filters?: HomeVisitPatientMassagerFilterParams
): Promise<HomeVisitPatientMassagersResponse> {
  const params = {
    visit_datetime_from: filters?.visit_datetime_from ?? undefined,
    visit_datetime_to: filters?.visit_datetime_to ?? undefined,
  };

  const response = await apiClient.get<HomeVisitPatientMassagersResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/home-visit-patients/massagers'),
    {
      params,
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Print patient visit schedule as PDF (一覧印刷).
 * Returns a Blob (application/pdf).
 */
export async function printHomeVisitPatients(
  siteType: string,
  clinicNumber: string,
  payload: PrintHomeVisitPatientPayload,
  serviceType: string = SERVICE_TYPE_A2
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/home-visit-patients/print'),
    payload,
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data as Blob;
}

/**
 * Export patient visit schedule as CSV (CSV出力).
 * Returns a Blob (text/csv).
 */
export async function exportHomeVisitPatientsCsv(
  siteType: string,
  clinicNumber: string,
  payload: ExportHomeVisitPatientCsvPayload,
  serviceType: string = SERVICE_TYPE_A2
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/home-visit-patients/export-csv'),
    payload,
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data as Blob;
}
